/**
 * Mission-area queries and mutations. Create/delete invalidate the shared
 * areas cache AND patch the shapes store directly, so the map reflects the
 * change immediately instead of waiting for the next 60s 'areas:update'
 * broadcast poll (which then reconciles authoritatively).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  queryKeys,
  hexToArgbInt,
  type AreaGeometry,
  type AreaKind,
  type MissionArea,
  type TakShape,
} from '@vtak/shared';
import { workspaceClient } from '@/services/workspace-client';
import { useShapesStore } from '@/stores/shapes-store';

/** Fields required to create a mission area. */
export interface NewAreaInput {
  name: string;
  kind: AreaKind;
  geometry: AreaGeometry;
  /** "#rrggbb" display color; omitted = kind default */
  color?: string;
}

/** Default display color per area kind (mirrors the server broadcast). */
const COLOR_BY_KIND: Record<AreaKind, string> = {
  area: '#38bdf8',
  'hazard-zone': '#f59e0b',
  closure: '#ef4444',
};

/** Fill alpha used by the areas broadcast (0x30 of 0xff). */
const FILL_OPACITY = 48 / 255;

/** Load the mission areas visible to the caller for a unit. */
export function useAreasQuery(unitId: string | null): UseQueryResult<MissionArea[]> {
  return useQuery({
    queryKey: queryKeys.workspace.areas(unitId ?? ''),
    queryFn: () => workspaceClient.listAreas(unitId ?? ''),
    enabled: Boolean(unitId),
  });
}

/** Create an area, refreshing the areas cache and the map layer on success. */
export function useCreateArea(unitId: string): UseMutationResult<MissionArea, Error, NewAreaInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NewAreaInput) => workspaceClient.createArea(unitId, input),
    onSuccess: (area) => {
      appendAreaShape(area);
      void client.invalidateQueries({ queryKey: queryKeys.workspace.areas(unitId) });
    },
  });
}

/** Delete an area, refreshing the areas cache and the map layer on success. */
export function useDeleteArea(unitId: string): UseMutationResult<{ ok: boolean }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string) => workspaceClient.deleteArea(unitId, areaId),
    onSuccess: (_result, areaId) => {
      removeAreaShape(areaId);
      void client.invalidateQueries({ queryKey: queryKeys.workspace.areas(unitId) });
    },
  });
}

/** Render a created area on the map ahead of the next areas broadcast. */
function appendAreaShape(area: MissionArea): void {
  const shape = areaToShape(area);
  if (!shape) return;
  const store = useShapesStore.getState();
  store.setAreaShapes([...store.areaShapes.filter((s) => s.uid !== shape.uid), shape]);
}

/** Drop a deleted area from the map ahead of the next areas broadcast. */
function removeAreaShape(areaId: string): void {
  const store = useShapesStore.getState();
  store.setAreaShapes(store.areaShapes.filter((s) => s.uid !== `area:${areaId}`));
}

/** Map a mission area to a renderable shape (mirrors the server broadcast). */
function areaToShape(area: MissionArea): TakShape | null {
  const color = area.color ?? COLOR_BY_KIND[area.kind];
  const base = {
    uid: `area:${area.id}`,
    callsign: area.name,
    strokeColor: hexToArgbInt(color, 1),
    strokeWeight: 2,
    fillColor: hexToArgbInt(color, FILL_OPACITY),
    createdAt: new Date(0).toISOString(),
  };
  if (area.geometry.shapeType === 'circle' && area.geometry.center && area.geometry.radiusMeters) {
    return {
      ...base,
      shapeType: 'circle',
      center: area.geometry.center,
      radiusMeters: area.geometry.radiusMeters,
    };
  }
  if (area.geometry.shapeType === 'polygon' && (area.geometry.vertices?.length ?? 0) >= 3) {
    return { ...base, shapeType: 'polygon', vertices: area.geometry.vertices ?? [] };
  }
  return null;
}
